import { memoize, orderBy } from "lodash";
import { observable, runInAction } from "mobx";
import {
  TOnCallData,
  TOnCallPackageNotificationTypeCount,
  TOnCallPaymentPackages,
  TOnCallPaymentPackagesToday,
  TStatsAll,
  TUsersData,
  TUsersSubscriptionsAndPurchases
} from "../../../Types";
import getCoreUIStore from "../../core/core-ui-store";
import { apiCallGetStats } from "../../../api/developer-api/api-call-get-stats";
import { isPositiveNumber, reduce } from "et3-type-guards";
import getCorePopUpStore from "../../core/core-popup-store";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../../helpers/et3-picker-data-helper";

class StatsStore {
  usersData = observable.box<TUsersData>([]);
  usersSubscriptionsAndPurchases =
    observable.box<TUsersSubscriptionsAndPurchases>([]);
  onCallData = observable.box<TOnCallData>([]);
  paymentPackages = observable.box<TOnCallPaymentPackages>([]);
  paymentPackagesToday = observable.box<TOnCallPaymentPackagesToday>([]);
  packageByTypeCount = observable.box<TOnCallPackageNotificationTypeCount>([]);
  statsLimit = observable.box<TEt3PickerSingleValue>({
    label: "10",
    value: "10"
  });

  setOnCallData = (data: TStatsAll) => {
    runInAction(() => {
      const usersData = data.usersData.map((item, index) => {
        return { id: index, ...item };
      });
      const sortedUsersData = orderBy(usersData, "id", "asc");
      this.usersData.set(sortedUsersData);

      const usersSubscriptionsAndPurchases =
        data.usersPurchasesAndSubscriptions.map((item, index) => {
          return { id: index, ...item };
        });
      const sortedUsersSubscriptionsAndPurchases = orderBy(
        usersSubscriptionsAndPurchases,
        "id",
        "asc"
      );
      this.usersSubscriptionsAndPurchases.set(
        sortedUsersSubscriptionsAndPurchases
      );

      const sortedOnCallData = orderBy(data.onCallData, "id", "asc");
      this.onCallData.set(sortedOnCallData);

      const paymentPackages = data.paymentPackages.map((item, index) => {
        return { id: index, ...item };
      });
      const paymentPackagesToday = data.paymentPackagesToday.map((item, index) => {
        return { id: index, ...item };
      });
      this.paymentPackages.set(paymentPackages);
      this.paymentPackagesToday.set(paymentPackagesToday);

      const packageNotificationTypeCount = data.packageNotificationTypeCount;

      const packagesByTypeCount = orderBy(
        packageNotificationTypeCount,
        "bundleCount",
        "asc"
      );
      this.packageByTypeCount.set(packagesByTypeCount);
    });
  };

  setStatsLimit = (limit: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.statsLimit.set(limit);
    });
  };

  getStats = async () => {
    const definedStatsLimit = reducerToZeroIfUndefined(
      parseInt(this.statsLimit.get().value)
    );

    if (!isPositiveNumber(definedStatsLimit)) {
      return;
    }

    getCoreUIStore().setIsLoading("DEVELOPER_STATS", true);
    const { status, data, code } = await apiCallGetStats(definedStatsLimit);
    getCoreUIStore().setIsLoading("DEVELOPER_STATS", false);
    if (status === "OK") {
      const usersData = reduce(() => data.usersData, []);
      const usersPurchasesAndSubscriptions = reduce(
        () => data.usersPurchasesAndSubscriptions,
        []
      );
      const onCallData = reduce(() => data.onCallData, []);
      const paymentPackages = reduce(() => data.paymentPackages, []);
      const paymentPackagesToday = reduce(() => data.paymentPackagesToday, []);
      const packageNotificationTypeCount = reduce(
        () => data.packageNotificationTypeCount,
        []
      );
      this.setOnCallData({
        usersData,
        usersPurchasesAndSubscriptions,
        onCallData,
        paymentPackages,
        packageNotificationTypeCount,
        paymentPackagesToday: paymentPackagesToday
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };
}
const getStatsStore = memoize(
  () => new StatsStore(),
  () => 1
);
export default getStatsStore;
